import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TabPanel, TabView } from 'primereact/tabview';
import { useQuery } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AbilityContext } from 'components/Can';
import { getProjectSumUp } from 'apis/projects.api';
import { ProjectGeneralInfo } from 'constant/project.constant';
import {
  CreateContract,
  MidtermCouncil,
  ExtendImplementationDetail,
  AcceptanceCouncil,
  LiquidationAgreement,
  LiquidationCouncil,
} from 'features/SubmitProjectProposal/components/SumUp';
import Loading from 'components/Loading';
import EditFinancialAdjustment from 'features/SubmitProjectProposal/pages/EditFinancialAdjustment';
import MidtermDocumentsChecker from '../MidtermDocumentsChecker';
import EditHumanResource from '../EditHumanResource';
// eslint-disable-next-line import/no-cycle
import { LiquidationDocumentsChecker } from '..';
import RegisterResultChecker from '../RegisterResultChecker';

function SubmitProjectProposalSumUp() {
  const { t } = useTranslation();
  // const { state } = useLocation();
  const navigate = useNavigate();
  const ability = useContext(AbilityContext);
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 0;
  const { projectId } = useParams();

  const { data: sumUpData, isLoading } = useQuery(['projectSumUp'], () =>
    getProjectSumUp(projectId)
  );
  const sumUp = useMemo(() => sumUpData?.data, [sumUpData]);

  const isLiquidation = useMemo(
    () => sumUp?.some((item) => item.type === ProjectGeneralInfo.LIQUIDATION),
    [sumUp]
  );

  function renderSumUpPanel(item, index) {
    /* Mảng sumUp = [extendImplementationTime, financialAdjustment,
   midtermSummaryReport, midtermCouncil, acceptanceSummaryReport,
   acceptanceCouncil,liquidationSummaryReport,  liquidationCouncil,registerResult, liquidation]
   */

    // Vị trí sumUp[index - 1] của midtermCouncil là midtermSummaryReport
    // Vị trí sumUp[index - 1] của acceptanceCouncil là acceptanceSummaryReport

    if (item?.data && item?.type === ProjectGeneralInfo.MIDTERM_SUMMARY_REPORT) {
      return (
        ability.can('read', 'summary-report') && (
          <TabPanel
            header={t('contract.sumUp.tabPanel.midtermSummaryReport')}
            leftIcon="pi pi-list mr-2"
          >
            <MidtermDocumentsChecker summaryReportId={item?.data?.id} projectId={projectId} />
          </TabPanel>
        )
      );
    }

    if (sumUp?.[index - 1]?.data && item?.type === ProjectGeneralInfo.MIDTERM_COUNCIL) {
      return (
        ability.can('read', 'council') && (
          <TabPanel header={t('contract.sumUp.tabPanel.midtermCouncil')} leftIcon="pi pi-list mr-2">
            <MidtermCouncil
              summaryReportId={item?.data?.summaryReport?.id ?? sumUp?.[index - 1]?.data?.id}
            />
          </TabPanel>
        )
      );
    }
    if (item?.data && item?.type === ProjectGeneralInfo.ACCEPTANCE_SUMMARY_REPORT) {
      return (
        ability.can('read', 'summary-report') && (
          <TabPanel
            header={t('contract.sumUp.tabPanel.acceptanceSummaryReport')}
            leftIcon="pi pi-list mr-2"
          >
            <MidtermDocumentsChecker summaryReportId={item?.data?.id} projectId={projectId} />
          </TabPanel>
        )
      );
    }
    if (sumUp?.[index - 1]?.data && item?.type === ProjectGeneralInfo.ACCEPTANCE_COUNCIL) {
      return (
        ability.can('read', 'council') && (
          <TabPanel
            header={t('contract.sumUp.tabPanel.acceptanceCouncil')}
            leftIcon="pi pi-list mr-2"
          >
            <AcceptanceCouncil
              summaryReportId={item?.data?.summaryReport?.id ?? sumUp?.[index - 1]?.data?.id}
            />
          </TabPanel>
        )
      );
    }
    if (item?.data && item?.type === ProjectGeneralInfo.FINANCIAL_ADJUSTMENT) {
      return (
        ability.can('read', 'project') && (
          <TabPanel
            header={t('contract.sumUp.tabPanel.financialAdjustment')}
            leftIcon="pi pi-list mr-2"
          >
            <EditFinancialAdjustment id={item?.data?.id} readOnly isSumUp />
          </TabPanel>
        )
      );
    }
    if (item?.data && item?.type === ProjectGeneralInfo.EXTEND_IMPLEMENTATION_TIME) {
      return (
        ability.can('read', 'project') && (
          <TabPanel
            header={t('contract.sumUp.tabPanel.extendImplementationTime')}
            leftIcon="pi pi-list mr-2"
          >
            <ExtendImplementationDetail id={item?.data?.id} isSumUp />
          </TabPanel>
        )
      );
    }
    if (item?.data && item?.type === ProjectGeneralInfo.PERSONNEL_ADJUSTMENT) {
      return (
        ability.can('read', 'project') && (
          <TabPanel
            header={t('contract.sumUp.tabPanel.personnelAdjustment')}
            leftIcon="pi pi-list mr-2"
          >
            <EditHumanResource id={item?.data?.id} isSumUp />
          </TabPanel>
        )
      );
    }
    if (item?.data && item?.type === ProjectGeneralInfo.REGISTER_RESULT) {
      return (
        ability.can('read', 'project') && (
          <TabPanel header={t('contract.sumUp.tabPanel.registerResult')} leftIcon="pi pi-list mr-2">
            <RegisterResultChecker isLiquidation={isLiquidation} isSumup />
          </TabPanel>
        )
      );
    }
    if (item?.data && item?.type === ProjectGeneralInfo.LIQUIDATION) {
      return (
        ability.can('read', 'liquidation') && (
          <TabPanel header={t('contract.sumUp.tabPanel.liquidation')} leftIcon="pi pi-list mr-2">
            <LiquidationAgreement />
          </TabPanel>
        )
      );
    }
    if (item?.data && item?.type === ProjectGeneralInfo.LIQUIDATION_SUMMARY_REPORT) {
      return (
        ability.can('read', 'summary-report') && (
          <TabPanel
            header={t('contract.sumUp.tabPanel.liquidationSummaryReport')}
            leftIcon="pi pi-list mr-2"
          >
            <LiquidationDocumentsChecker summaryReportId={item?.data?.id} projectId={projectId} />
          </TabPanel>
        )
      );
    }
    if (sumUp?.[index - 1]?.data && item?.type === ProjectGeneralInfo.LIQUIDATION_COUNCIL) {
      return (
        ability.can('read', 'liquidation') && (
          <TabPanel
            header={t('contract.sumUp.tabPanel.liquidationCouncil')}
            leftIcon="pi pi-list mr-2"
          >
            <LiquidationCouncil
              summaryReportId={item?.data?.summaryReport?.id ?? sumUp?.[index - 1]?.data?.id}
            />
          </TabPanel>
        )
      );
    }
    return null;
  }
  return (
    <>
      {isLoading && <Loading />}
      <TabView
        scrollable
        activeIndex={parseInt(tab, 10)}
        onTabChange={(e) => navigate(`.?tab=${e.index}`)}
      >
        {ability.can('read', 'project') && (
          <TabPanel header={t('contract.name')} leftIcon="pi pi-list mr-2">
            <CreateContract disabled />
          </TabPanel>
        )}
        {sumUp?.length && sumUp.map((item, index) => renderSumUpPanel(item, index))}
      </TabView>
    </>
  );
}

export default SubmitProjectProposalSumUp;
